import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';
import { useReducedMotion } from '@/scroll/use-reduced-motion';

export interface MarketTab {
  id: string;
  label: string;
}

interface MarketTabsProps {
  tabs: readonly MarketTab[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Namespaces the tab and panel ids: `<idPrefix>-tab-<id>` controls `<idPrefix>-panel-<id>`. */
  idPrefix: string;
  /** Accessible name of the tablist. */
  label: string;
  className?: string;
}

/** Which edges of the strip hide more tabs behind them. */
type Fade = 'none' | 'start' | 'end' | 'both';

function readFade(scroller: HTMLElement): Fade {
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  if (maxScroll <= 1) return 'none';
  const start = scroller.scrollLeft > 1;
  const end = scroller.scrollLeft < maxScroll - 1;
  if (start && end) return 'both';
  if (start) return 'start';
  return end ? 'end' : 'none';
}

export function tabId(idPrefix: string, id: string): string {
  return `${idPrefix}-tab-${id}`;
}

export function panelId(idPrefix: string, id: string): string {
  return `${idPrefix}-panel-${id}`;
}

/**
 * Underlined tab strip of markets on one row. The strip scrolls horizontally once it
 * overflows, fades the edge that hides more tabs and brings the selected tab into view;
 * Lenis leaves horizontal gestures over it alone. Arrow, Home and End keys move the
 * selection with a roving tabIndex.
 */
export function MarketTabs({
  tabs,
  activeId,
  onSelect,
  idPrefix,
  label,
  className,
}: MarketTabsProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const reduced = useReducedMotion();
  const [fade, setFade] = useState<Fade>('none');

  const updateFade = useCallback(() => {
    if (scrollerRef.current) setFade(readFade(scrollerRef.current));
  }, []);

  useEffect(() => {
    updateFade();
    const scroller = scrollerRef.current;
    const row = rowRef.current;
    if (!scroller || !row) return;
    // The row too: web fonts widen the tabs after mount without resizing the scroller itself.
    const observer = new ResizeObserver(updateFade);
    observer.observe(scroller);
    observer.observe(row);
    return () => observer.disconnect();
  }, [updateFade, tabs.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const tab = buttonsRef.current[tabs.findIndex((t) => t.id === activeId)];
    if (!scroller || !tab || scroller.scrollWidth - scroller.clientWidth <= 1) return;
    // Scroll only the strip, never the page, so keyboard users are not yanked around.
    const left = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
    scroller.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }, [activeId, tabs, reduced]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = tabs.length;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % count;
    else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    else return;
    event.preventDefault();
    onSelect(tabs[next].id);
    buttonsRef.current[next]?.focus();
  };

  return (
    <div className={cn('relative border-b border-border', className)}>
      <div
        ref={scrollerRef}
        onScroll={updateFade}
        data-fade={fade}
        data-lenis-prevent-horizontal
        className={cn(
          'relative flex overflow-x-auto overscroll-x-contain',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'data-[fade=start]:[mask-image:linear-gradient(to_right,transparent,#000_2.5rem)]',
          'data-[fade=end]:[mask-image:linear-gradient(to_right,#000_calc(100%-2.5rem),transparent)]',
          'data-[fade=both]:[mask-image:linear-gradient(to_right,transparent,#000_2.5rem,#000_calc(100%-2.5rem),transparent)]',
        )}
      >
        <div ref={rowRef} role="tablist" aria-label={label} className="flex min-w-max gap-x-6">
          {tabs.map((tab, index) => {
            const selected = tab.id === activeId;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  buttonsRef.current[index] = el;
                }}
                id={tabId(idPrefix, tab.id)}
                role="tab"
                type="button"
                aria-selected={selected}
                // Only the selected market's panel is mounted; the others' ids would dangle.
                aria-controls={selected ? panelId(idPrefix, tab.id) : undefined}
                tabIndex={selected ? 0 : -1}
                onClick={() => onSelect(tab.id)}
                onKeyDown={(event) => onKeyDown(event, index)}
                className={cn(
                  'relative shrink-0 whitespace-nowrap px-0.5 pb-3 text-sm font-semibold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  // The underline scales in from the centre; the label only changes colour.
                  'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:origin-center after:scale-x-0 after:bg-primary after:duration-500 after:ease-out-expo after:content-[""] motion-safe:after:transition-transform',
                  selected
                    ? 'text-foreground after:scale-x-100'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
